import React, { useEffect, useState } from "react";
import {
  SimpleGrid,
  Text,
  Spinner,
  Badge,
  Box,
  Flex,
  IconButton,
  Input,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  HStack,
  Tooltip,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FaFolder, FaList } from "react-icons/fa";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import Card from "../components/Card/Card";
import CardHeader from "../components/Card/CardHeader";
import CardBody from "../components/Card/CardBody";
import { useProjectList } from "../context/ProjectListContext";
import { useTranslation } from "react-i18next";
import marketplaceFlags from "../countryCodes";

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const { currentUser, getToken, userPlan } = useAuth();
  const { projects, loading: projectsLoading, setProjects, refreshProjects } = useProjectList();
  
  const isLimitReached = userPlan === 'free' && projects.length >= 1;
  const navigate = useNavigate();

  useEffect(() => {
    if (!projectsLoading && projects.length === 0) {
      navigate("/create");
    }
  }, [projectsLoading, projects, navigate]);

  const [loading, setLoading] = useState<boolean>(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [editingProjectName, setEditingProjectName] = useState<string>("");
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  const { projectFolderId } = useParams<{ projectFolderId: string }>();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshProjects();
    }, 10000);
    return () => clearInterval(interval);
  }, [refreshProjects]);

  const handleEditProject = (projectId: string, newName: string) => {
    setProjects((prevProjects) =>
      prevProjects.map((project) =>
        project.id === projectId ? { ...project, name: newName } : project,
      ),
    );
  };

  const handleDeleteProject = (projectId: string) => {
    setProjects((prevProjects) =>
      prevProjects.filter((project) => project.id !== projectId),
    );
  };

  const confirmEditProject = async () => {
    const token = await getToken();
    try {
      const response = await fetch("/.netlify/functions/editProject", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId: editingProjectId,
          newName: editingProjectName,
        }),
      });

      if (!response.ok) {
        throw new Error(t("errorEditProject"));
      }

      handleEditProject(editingProjectId!, editingProjectName);
      setEditingProjectId(null);
    } catch (error) {
      console.error(t("errorEditProject"), error);
    }
  };

  const renderImage = ({
    emoji = "",
    name = "",
    asin = "",
    data = { main_image: { link: "" } },
  }) => {
    if (data) {
      const { main_image = { link: "" } } = data;
      const { link = "" } = main_image;
      if (asin && link) {
        return (
          <Box
            height="40px"
            width="40px"
            minW="40px"
            borderRadius="md"
            overflow="hidden"
            bg="white"
          >
            <img
              src={
                typeof data.main_image === 'object' && 'link' in data.main_image
                  ? data.main_image.link
                  : (data.main_image as string) ?? ''
              }
              alt={name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          </Box>
        );
      }
    }

    return (
      <Box
        height="40px"
        width="40px"
        minW="40px"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xl"
        bg="gray.700"
        borderRadius="md"
      >
        <Text>{emoji}</Text>
      </Box>
    );
  };

  const confirmDeleteProject = async () => {
    const token = await getToken();
    try {
      const response = await fetch("/.netlify/functions/deleteProject", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId: deletingProjectId,
        }),
      });

      if (!response.ok) {
        throw new Error(t("errorDeleteProject"));
      }

      handleDeleteProject(deletingProjectId!);
      onClose();
    } catch (error) {
      console.error(t("errorDeleteProject"), error);
    }
  };

  const getFilteredItems = () => {
    const projectList = [] as any[];
    const folderList = [] as any[];
    const parentSet = new Set<string>();

    projects.forEach((project) => {
      if (project.originalProjectId) {
        parentSet.add(project.originalProjectId);
      }
    });

    if (projects.length > 0) {
      projects.forEach((project) => {
        if (
          (!parentSet.has(project.id) &&
            !parentSet.has(project.originalProjectId as string)) ||
          projectFolderId
        ) {
          if (
            !projectFolderId ||
            projectFolderId === project.originalProjectId ||
            projectFolderId === project.id
          )
            projectList.push({ type: 'project', data: project });
        } else if (!project.originalProjectId && !projectFolderId) {
          folderList.push({ type: 'folder', data: project });
        }
      });
    }

    return [...folderList, ...projectList];
  };

  const allItems = getFilteredItems();
  const totalPages = Math.ceil(allItems.length / itemsPerPage) || 1;

  const paginatedItems = allItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const renderPagination = () => {
    if (allItems.length === 0) return null;
    
    // Limiter le nombre de boutons de page affichés
    const maxPageButtons = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);
    if (endPage - startPage + 1 < maxPageButtons) {
      startPage = Math.max(1, endPage - maxPageButtons + 1);
    }
    const pageNumbers = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);

    return (
      <Flex justify="space-between" align="center" mt={4} flexWrap="wrap" gap={2}>
        <Text fontSize="sm" color="gray.500">
          {t("showing")} {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, allItems.length)} {t("of")} {allItems.length}
        </Text>
        <HStack spacing={1}>
          <IconButton
            aria-label="Previous page"
            icon={<ChevronLeftIcon />}
            isDisabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => prev - 1)}
            size="sm"
            variant="ghost"
          />
          {pageNumbers.map(page => (
            <Button
              key={page}
              size="sm"
              variant={currentPage === page ? "solid" : "ghost"}
              colorScheme={currentPage === page ? "teal" : "gray"}
              onClick={() => setCurrentPage(page)}
              minW="32px"
            >
              {page}
            </Button>
          ))}
          <IconButton
            aria-label="Next page"
            icon={<ChevronRightIcon />}
            isDisabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => prev + 1)}
            size="sm"
            variant="ghost"
          />
        </HStack>
      </Flex>
    );
  };

  const renderGridView = () => (
    <SimpleGrid
      columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
      spacing={5}
    >
      {!projectFolderId && currentPage === 1 && (
        <Tooltip label={isLimitReached ? (t("upgradeToCreateMore") || "Limite atteinte. Passez Pro pour créer plus de projets.") : ""} isDisabled={!isLimitReached}>
            <Box position="relative" height="100%"> 
                {/* Wrapper Box to ensure Tooltip works and Grid isn't broken, though Card is direct child of Grid usually */}
                <Card
                className="dashcard project create"
                key="create"
                onClick={() => !isLimitReached && navigate(`/create`)}
                p={0}
                cursor={isLimitReached ? "not-allowed" : "pointer"}
                opacity={isLimitReached ? 0.5 : 1}
                h="100%"
                >
                <CardHeader display="flex" alignItems="center" justifyContent="center">
                    {renderImage({ emoji: "+" })}
                </CardHeader>
                <CardBody className="productLabel" p={0}>
                    <Text className="productTitle" fontWeight="bold" textAlign="center">
                    {t("createProject")}
                    </Text>
                </CardBody>
                </Card>
            </Box>
        </Tooltip>
      )}

      {paginatedItems.map((item) => {
        const project = item.data;
        const isFolder = item.type === 'folder';

        return (
          <Card
            className={`dashcard project ${isFolder ? 'folder' : ''}`}
            key={project.id}
            onClick={() => isFolder ? navigate(`/folder/${project.id}`) : navigate(`/projects/${project.id}`)}
            p={0}
            cursor="pointer"
          >
            <CardHeader display="flex" alignItems="center" justifyContent="center">
              {project.building ? (
                <Flex direction="column" align="center" justify="center" h={{ base: "200px", md: "150px" }} w="100%" bg="gray.700" borderRadius="lg">
                  <Spinner size="lg" color="teal.400" thickness="3px" mb={3} />
                  <Text fontSize="xs" color="gray.400" fontWeight="bold" textTransform="uppercase">
                    {t('building') || "Création..."}
                  </Text>
                </Flex>
              ) : (
                renderImage(project)
              )}
            </CardHeader>
            <CardBody className="productLabel" p={0}>
              {editingProjectId === project.id ? (
                <Input
                  onClick={(e) => e.stopPropagation()}
                  value={editingProjectName}
                  onChange={(e) => setEditingProjectName(e.target.value)}
                  onBlur={confirmEditProject}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") setEditingProjectId(null);
                    if (e.key === "Enter") confirmEditProject();
                  }}
                  autoFocus
                />
              ) : (
                <Text className="productTitle" fontWeight="bold">
                  {project.name || t("untitledProject")}
                </Text>
              )}

              {!isFolder && (
                <Flex className="buttons" ml="auto">
                  <IconButton
                    aria-label={t("editProject")}
                    icon={<EditIcon />}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingProjectId(project.id);
                      setEditingProjectName(project.name || "");
                    }}
                    variant="ghost"
                  />
                  <IconButton
                    aria-label={t("deleteProject")}
                    icon={<DeleteIcon />}
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeletingProjectId(project.id);
                      onOpen();
                    }}
                    variant="ghost"
                  />
                </Flex>
              )}

              <Text className="asin" color="gray.500">
                {isFolder ? (
                  <Badge>{t("multilingual")}</Badge>
                ) : (
                  <Badge>
                    {marketplaceFlags[String(project.marketplace).toLowerCase() || "fr"]}
                  </Badge>
                )}
                <Badge ml={1}>{project.asin || t("creationMode")}</Badge>
              </Text>
            </CardBody>
          </Card>
        );
      })}
    </SimpleGrid>
  );

  const renderListView = () => (
    <Card p="22px" overflow="hidden">
      <CardHeader p="0 0 20px 0">
        <Flex justify="space-between" align="center" w="100%">
          <Text fontSize="lg" color="#fff" fontWeight="bold">
            {projectFolderId ? t("folderContent") : t("projectsList")}
          </Text>
          <Tooltip label={isLimitReached ? (t("upgradeToCreateMore") || "Limite atteinte.") : ""} isDisabled={!isLimitReached}>
            <Button size="sm" colorScheme="primary" onClick={() => navigate('/create')} isDisabled={isLimitReached}>
                + {t("createProject")}
            </Button>
          </Tooltip>
        </Flex>
      </CardHeader>
      <CardBody p="0" flexDirection="column">
        <Box overflowX="auto" w="100%">
          <Table variant="simple" color="#fff" size="sm" sx={{ tableLayout: "fixed" }} w="100%">
            <Thead>
              <Tr>
                <Th color="gray.400" borderBottomColor="#56577A" w="40%">{t("project")}</Th>
                <Th color="gray.400" borderBottomColor="#56577A" w="12%">{t("statusHeader")}</Th>
                <Th color="gray.400" borderBottomColor="#56577A" w="15%">{t("marketplaceHeader")}</Th>
                <Th color="gray.400" borderBottomColor="#56577A" w="18%">{t("asinHeader")}</Th>
                <Th color="gray.400" borderBottomColor="#56577A" w="15%">{t("actionsHeader")}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {paginatedItems.map((item) => {
                const project = item.data;
                const isFolder = item.type === 'folder';

                return (
                  <Tr
                    key={project.id}
                    _hover={{ bg: "whiteAlpha.100", cursor: "pointer" }}
                    onClick={() => isFolder ? navigate(`/folder/${project.id}`) : navigate(`/projects/${project.id}`)}
                  >
                    <Td borderBottomColor="#56577A" py={3} overflow="hidden">
                      <Flex align="center" gap={3}>
                        <Box flexShrink={0}>
                          {isFolder ? (
                            <Box fontSize="20px" color="teal.300">
                              <FaFolder />
                            </Box>
                          ) : (
                            renderImage(project)
                          )}
                        </Box>
                        <Box overflow="hidden" flex="1">
                          {editingProjectId === project.id ? (
                            <Input
                              onClick={(e) => e.stopPropagation()}
                              value={editingProjectName}
                              onChange={(e) => setEditingProjectName(e.target.value)}
                              onBlur={confirmEditProject}
                              onKeyDown={(e) => {
                                if (e.key === "Escape") setEditingProjectId(null);
                                if (e.key === "Enter") confirmEditProject();
                              }}
                              autoFocus
                              size="sm"
                              color="white"
                            />
                          ) : (
                            <Text 
                              fontSize="sm" 
                              color="#fff" 
                              fontWeight="bold" 
                              overflow="hidden" 
                              textOverflow="ellipsis" 
                              whiteSpace="nowrap"
                            >
                              {project.name || t("untitledProject")}
                            </Text>
                          )}
                          {project.building && (
                            <Text fontSize="xs" color="teal.400" fontWeight="bold">
                              {t('building') || "Création..."}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    </Td>
                    <Td borderBottomColor="#56577A" py={3}>
                      {isFolder ? (
                        <Badge colorScheme="blue" borderRadius="full" px={2}>{t("multilingual")}</Badge>
                      ) : (
                        <Badge colorScheme={project.building ? "orange" : "green"} borderRadius="full" px={2}>
                            {project.building ? t("processing") : t("ready")}
                        </Badge>
                      )}
                    </Td>
                    <Td borderBottomColor="#56577A" py={3}>
                      {!isFolder && (
                        <Flex align="center" gap={2}>
                          <Text fontSize="lg">
                            {marketplaceFlags[String(project.marketplace).toLowerCase() || "fr"]}
                          </Text>
                          <Text fontSize="xs" color="gray.400">
                            {String(project.marketplace).toUpperCase() || "FR"}
                          </Text>
                        </Flex>
                      )}
                    </Td>
                    <Td borderBottomColor="#56577A" py={3} overflow="hidden">
                      <Text 
                        fontSize="sm" 
                        color="#fff" 
                        fontWeight="bold"
                        overflow="hidden"
                        textOverflow="ellipsis"
                        whiteSpace="nowrap"
                      >
                        {project.asin || "-"}
                      </Text>
                    </Td>
                    <Td borderBottomColor="#56577A" py={3}>
                      <Flex gap={1}>
                        <IconButton
                          aria-label={t("editProject")}
                          icon={<EditIcon />}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingProjectId(project.id);
                            setEditingProjectName(project.name || "");
                          }}
                          variant="ghost"
                          colorScheme="primary"
                        />
                        <IconButton
                          aria-label={t("deleteProject")}
                          icon={<DeleteIcon />}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingProjectId(project.id);
                            onOpen();
                          }}
                          variant="ghost"
                          colorScheme="red"
                        />
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
        {renderPagination()}
      </CardBody>
    </Card>
  );

  if (loading || projectsLoading) {
    return (
      <Flex align="center" justify="center" h="100vh">
        <Spinner size="xl" />
      </Flex>
    );
  }

  return (
    <Box w="100%" overflow="hidden">
      {renderListView()}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("deleteProjectTitle")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{t("deleteProjectConfirmation")}</Text>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={confirmDeleteProject}>
              {t("delete")}
            </Button>
            <Button variant="ghost" onClick={onClose}>
              {t("cancel")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default Dashboard;